import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatSize } from "@/lib/format";
import {
	type NetworkBodyPreview,
	type NetworkHarEntry,
	type NetworkNameValue,
	type NetworkRequestRecord,
	formatEscapedText,
} from "@/lib/networkInspection";
import { cn } from "@/lib/utils";
import { AlertTriangle } from "lucide-react";
import { useMemo, useState } from "react";

export function NetworkRequestInspector({
	requests,
	warnings = [],
	emptyMessage = "No inspected requests yet.",
	variant = "full",
	embedded = false,
}: {
	requests: NetworkRequestRecord[];
	warnings?: string[];
	emptyMessage?: string;
	// "full" renders a list beside a detail pane; "compact" renders only the
	// list and opens each request's details in a popover, for narrow side
	// panels where a split view cannot fit.
	variant?: "full" | "compact";
	embedded?: boolean;
}) {
	const [selectedId, setSelectedId] = useState<string>();
	const selected = useMemo(
		() =>
			requests.find((request) => request.id === selectedId) ??
			requests[requests.length - 1],
		[requests, selectedId],
	);
	const effectiveSelectedId = selected?.id;

	if (variant === "compact") {
		return (
			<div className={cn("flex h-full min-h-0 flex-col overflow-hidden bg-background", !embedded && "min-h-[12rem] rounded-lg border")}>
				{warnings.length > 0 && (
					<div className="space-y-2 border-b p-3">
						{warnings.map((warning) => (
							<Alert key={warning} className="py-2">
								<AlertTriangle className="h-4 w-4" />
								<AlertTitle>Capture may be incomplete</AlertTitle>
								<AlertDescription>{warning}</AlertDescription>
							</Alert>
						))}
					</div>
				)}
				<CompactRequestList requests={requests} emptyMessage={emptyMessage} dense={embedded} />
			</div>
		);
	}

	return (
		<div className="flex min-h-[26rem] flex-col overflow-hidden rounded-lg border bg-background">
			{warnings.length > 0 && (
				<div className="space-y-2 border-b p-3">
					{warnings.map((warning) => (
						<Alert key={warning} className="py-2">
							<AlertTriangle className="h-4 w-4" />
							<AlertTitle>Capture may be incomplete</AlertTitle>
							<AlertDescription>{warning}</AlertDescription>
						</Alert>
					))}
				</div>
			)}
			<div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(24rem,42%)_1fr]">
				<div className="flex min-h-[18rem] min-w-0 flex-col border-b lg:border-b-0 lg:border-r">
					<RequestList
						requests={requests}
						selectedId={effectiveSelectedId}
						onSelect={setSelectedId}
						emptyMessage={emptyMessage}
					/>
				</div>
				<div className="min-h-[18rem] min-w-0">
					{selected ? (
						<RequestDetails request={selected} />
					) : (
						<p className="p-4 text-sm text-muted-foreground">
							Select a request to inspect it.
						</p>
					)}
				</div>
			</div>
		</div>
	);
}

// CompactRequestList is the narrow-panel request list: each row opens its
// details in a popover that stays open until dismissed, so headers can be
// read and Copy as cURL clicked without a detail pane taking width.
function CompactRequestList({
	requests,
	emptyMessage,
	dense = false,
}: {
	requests: NetworkRequestRecord[];
	emptyMessage: string;
	dense?: boolean;
}) {
	const [openId, setOpenId] = useState<string>();
	if (requests.length === 0) {
		return <p className="p-4 text-sm text-muted-foreground">{emptyMessage}</p>;
	}
	return (
		<>
			<div className="grid grid-cols-[3.5rem_3rem_minmax(0,1fr)_3.5rem] gap-2 border-b bg-muted/50 px-3 py-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
				<span>Method</span>
				<span>Status</span>
				<span>Path</span>
				<span className="text-right">Time</span>
			</div>
			<ScrollArea className="min-h-0 flex-1">
				<div role="list" aria-label="Network requests">
					{requests.map((request) => {
						const location = requestLocation(request.entry.request.url);
						return (
							<Popover
								key={request.id}
								open={openId === request.id}
								onOpenChange={(open) => setOpenId(open ? request.id : undefined)}
							>
								<PopoverTrigger asChild>
									<button
										type="button"
										aria-selected={request.id === openId}
										className={cn(
											"grid w-full grid-cols-[3.5rem_3rem_minmax(0,1fr)_3.5rem] gap-2 border-b px-3 py-2 text-left text-xs transition-colors hover:bg-muted/50",
											request.id === openId && "bg-muted",
											dense && "px-2 py-1 text-[11px]",
										)}
									>
										<span className="truncate font-mono font-medium">
											{request.entry.request.method}
										</span>
										<span className={statusClass(request.entry.response.status)}>
											{request.entry._limrun.error
												? "ERR"
												: request.entry.response.status || "—"}
										</span>
										<span
											className="truncate font-mono"
											title={`${location.host}${location.path}`}
										>
											{location.path}
										</span>
										<span className="text-right tabular-nums">
											{formatDuration(request.entry.time)}
										</span>
									</button>
								</PopoverTrigger>
								<PopoverContent
									side="left"
									align="start"
									className="h-[28rem] w-[30rem] max-w-[90vw] overflow-hidden p-0"
								>
									<RequestDetails request={request} />
								</PopoverContent>
							</Popover>
						);
					})}
				</div>
			</ScrollArea>
		</>
	);
}

function RequestList({
	requests,
	selectedId,
	onSelect,
	emptyMessage,
}: {
	requests: NetworkRequestRecord[];
	selectedId: string | undefined;
	onSelect: (id: string) => void;
	emptyMessage: string;
}) {
	if (requests.length === 0) {
		return <p className="p-4 text-sm text-muted-foreground">{emptyMessage}</p>;
	}
	return (
		<>
			<div className="grid grid-cols-[4.5rem_3.5rem_minmax(7rem,1fr)_minmax(8rem,1.4fr)_4rem_4.5rem] gap-2 border-b bg-muted/50 px-3 py-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
				<span>Method</span>
				<span>Status</span>
				<span>Host</span>
				<span>Path</span>
				<span className="text-right">Time</span>
				<span className="text-right">Size</span>
			</div>
			<ScrollArea className="min-h-0 flex-1">
				<div role="list" aria-label="Network requests">
					{requests.map((request) => {
						const location = requestLocation(request.entry.request.url);
						return (
							<button
								key={request.id}
								type="button"
								aria-selected={request.id === selectedId}
								onClick={() => onSelect(request.id)}
								className={cn(
									"grid w-full grid-cols-[4.5rem_3.5rem_minmax(7rem,1fr)_minmax(8rem,1.4fr)_4rem_4.5rem] gap-2 border-b px-3 py-2 text-left text-xs transition-colors hover:bg-muted/50",
									request.id === selectedId && "bg-muted",
								)}
							>
								<span className="truncate font-mono font-medium">
									{request.entry.request.method}
								</span>
								<span className={statusClass(request.entry.response.status)}>
									{request.entry._limrun.error
										? "ERR"
										: request.entry.response.status || "—"}
								</span>
								<span className="truncate" title={location.host}>
									{location.host}
								</span>
								<span className="truncate font-mono" title={location.path}>
									{location.path}
								</span>
								<span className="text-right tabular-nums">
									{formatDuration(request.entry.time)}
								</span>
								<span className="text-right tabular-nums">
									{formatBytes(request.entry.response.bodySize)}
								</span>
							</button>
						);
					})}
				</div>
			</ScrollArea>
		</>
	);
}

function RequestDetails({ request }: { request: NetworkRequestRecord }) {
	const { entry } = request;
	const location = requestLocation(entry.request.url);
	const requestTruncated =
		request.requestBody?.truncated === true ||
		entry._limrun.requestBodyTruncated === true;
	const responseTruncated =
		request.responseBody?.truncated === true ||
		entry._limrun.responseBodyTruncated === true;
	return (
		<div className="flex h-full min-h-0 flex-col">
			<div className="space-y-1 border-b px-4 py-3">
				<div className="flex flex-wrap items-center gap-2">
					<Badge variant="outline" className="font-mono">
						{entry.request.method}
					</Badge>
					<span className="text-sm font-semibold">
						{entry._limrun.error
							? "Request failed"
							: `${entry.response.status} ${entry.response.statusText}`}
					</span>
					<span className="ml-auto text-xs text-muted-foreground">
						{formatDuration(entry.time)} ·{" "}
						{formatBytes(entry.response.bodySize)}
					</span>
					<CopyAsCurlButton request={request} />
				</div>
				<p className="break-all font-mono text-xs">
					{location.host}
					{location.path}
				</p>
				<div className="flex flex-wrap gap-x-3 text-[11px] text-muted-foreground">
					<span>Tunnel {entry._limrun.tunnelId || "unknown"}</span>
					<span>Selector {entry._limrun.selectorId || "unknown"}</span>
					{entry.serverIPAddress && <span>Server {entry.serverIPAddress}</span>}
				</div>
				{entry._limrun.error && (
					<p className="text-xs text-red-600">{entry._limrun.error}</p>
				)}
			</div>
			{(requestTruncated || responseTruncated) && (
				<div className="border-b p-3">
					<Alert className="py-2">
						<AlertTriangle className="h-4 w-4" />
						<AlertTitle>Body preview truncated</AlertTitle>
						<AlertDescription>
							{requestTruncated && responseTruncated
								? "Request and response bodies exceed capture or UI preview limits."
								: requestTruncated
									? "The request body exceeds capture or UI preview limits."
									: "The response body exceeds capture or UI preview limits."}
						</AlertDescription>
					</Alert>
				</div>
			)}
			<Tabs defaultValue="headers" className="flex min-h-0 flex-1 flex-col p-3">
				<TabsList className="w-fit">
					<TabsTrigger value="headers">Headers</TabsTrigger>
					<TabsTrigger value="timing">Timing</TabsTrigger>
					<TabsTrigger value="request">Request body</TabsTrigger>
					<TabsTrigger value="response">Response body</TabsTrigger>
				</TabsList>
				<TabsContent value="headers" className="min-h-0 flex-1">
					<ScrollArea className="h-full max-h-[28rem]">
						<div className="space-y-4 pr-3">
							<HeaderSection
								title="Request headers"
								headers={entry.request.headers}
							/>
							<HeaderSection
								title="Response headers"
								headers={entry.response.headers}
							/>
							{entry._limrun.responseTrailers &&
								entry._limrun.responseTrailers.length > 0 && (
									<HeaderSection
										title="Response trailers"
										headers={entry._limrun.responseTrailers}
									/>
								)}
						</div>
					</ScrollArea>
				</TabsContent>
				<TabsContent value="timing" className="min-h-0 flex-1">
					<TimingDetails entry={entry} />
				</TabsContent>
				<TabsContent value="request" className="min-h-0 flex-1">
					<BodyDetails body={request.requestBody} />
				</TabsContent>
				<TabsContent value="response" className="min-h-0 flex-1">
					<BodyDetails body={request.responseBody} />
				</TabsContent>
			</Tabs>
		</div>
	);
}

// Headers curl derives itself are dropped: a verbatim content-length would
// break the replay when curl re-computes the body length, and host comes
// from the URL.
const curlSkippedHeaders = new Set(["host", "content-length", "connection"]);

function shellQuote(value: string): string {
	return `'${value.replace(/'/g, `'\\''`)}'`;
}

// curlCommand rebuilds the request as a curl invocation, like the browser
// network tab's "Copy as cURL". The body is included only when it was
// captured whole as text; a truncated or binary preview would replay a
// different request than the one inspected.
function curlCommand(request: NetworkRequestRecord): string {
	const req = request.entry.request;
	const parts: string[] = [`curl ${shellQuote(req.url)}`];
	if (req.method !== "GET") parts.push(`-X ${req.method}`);
	for (const header of req.headers) {
		if (curlSkippedHeaders.has(header.name.toLowerCase())) continue;
		parts.push(`-H ${shellQuote(`${header.name}: ${header.value}`)}`);
	}
	const body = request.requestBody;
	if (body && body.encoding === "text" && !body.truncated) {
		parts.push(`--data-raw ${shellQuote(body.text)}`);
	}
	return parts.join(" \\\n  ");
}

function CopyAsCurlButton({ request }: { request: NetworkRequestRecord }) {
	const [copied, setCopied] = useState(false);
	const bodyOmitted =
		request.requestBody !== undefined &&
		(request.requestBody.encoding !== "text" || request.requestBody.truncated);
	return (
		<button
			type="button"
			onClick={() => {
				navigator.clipboard.writeText(curlCommand(request)).then(() => {
					setCopied(true);
					setTimeout(() => setCopied(false), 1_500);
				});
			}}
			title={
				bodyOmitted
					? "Copies method, URL and headers; the request body is omitted because it was captured truncated or as binary."
					: "Copy this request as a curl command."
			}
			className="rounded-md border border-input bg-background px-2 py-0.5 text-xs font-medium hover:bg-muted"
		>
			{copied ? "Copied" : "Copy as cURL"}
		</button>
	);
}

function HeaderSection({
	title,
	headers,
}: {
	title: string;
	headers: NetworkNameValue[];
}) {
	return (
		<section>
			<h3 className="mb-2 text-xs font-semibold">{title}</h3>
			{headers.length === 0 ? (
				<p className="text-xs text-muted-foreground">No headers.</p>
			) : (
				<dl className="grid grid-cols-[minmax(8rem,30%)_1fr] gap-x-3 gap-y-1 font-mono text-xs">
					{headers.map((header, index) => (
						<div
							key={`${header.name}-${index}`}
							className="col-span-2 grid grid-cols-subgrid"
						>
							<dt className="break-all text-muted-foreground">{header.name}</dt>
							<dd className="break-all">{header.value}</dd>
						</div>
					))}
				</dl>
			)}
		</section>
	);
}

function TimingDetails({ entry }: { entry: NetworkHarEntry }) {
	const timingRows = Object.entries(entry.timings);
	return (
		<dl className="grid max-w-md grid-cols-2 gap-x-8 gap-y-2 text-sm">
			{timingRows.map(([name, duration]) => (
				<div
					key={name}
					className="col-span-2 grid grid-cols-subgrid border-b py-1"
				>
					<dt className="capitalize text-muted-foreground">{name}</dt>
					<dd className="text-right tabular-nums">
						{duration < 0 ? "Unavailable" : `${duration} ms`}
					</dd>
				</div>
			))}
			<div className="col-span-2 grid grid-cols-subgrid pt-1 font-medium">
				<dt>Total</dt>
				<dd className="text-right tabular-nums">
					{formatDuration(entry.time)}
				</dd>
			</div>
		</dl>
	);
}

function BodyDetails({ body }: { body: NetworkBodyPreview | undefined }) {
	if (!body) {
		return (
			<p className="text-sm text-muted-foreground">No body was captured.</p>
		);
	}
	return (
		<div className="flex h-full min-h-0 flex-col gap-2">
			<div className="flex items-center gap-2 text-xs text-muted-foreground">
				<Badge variant="outline">
					{body.encoding === "base64" ? "Binary · base64" : "Text · escaped"}
				</Badge>
				<span>{formatSize(body.byteLength)} captured</span>
				{body.truncated && <span className="text-amber-700">truncated</span>}
			</div>
			<ScrollArea className="min-h-0 flex-1 rounded-md border bg-muted/30">
				<pre
					aria-label={
						body.encoding === "base64"
							? "Base64 body preview"
							: "Escaped text body preview"
					}
					className="whitespace-pre-wrap break-all p-3 font-mono text-xs"
				>
					{body.encoding === "text" ? formatEscapedText(body.text) : body.text}
				</pre>
			</ScrollArea>
		</div>
	);
}

function requestLocation(rawUrl: string): { host: string; path: string } {
	try {
		const url = new URL(rawUrl);
		return { host: url.host, path: `${url.pathname}${url.search}` || "/" };
	} catch {
		return { host: "invalid URL", path: rawUrl };
	}
}

function statusClass(status: number): string {
	if (status >= 500 || status === 0) return "font-medium text-red-600";
	if (status >= 400) return "font-medium text-amber-700";
	if (status >= 300) return "font-medium text-blue-600";
	return "font-medium text-emerald-700";
}

function formatDuration(milliseconds: number): string {
	if (milliseconds < 1_000) return `${Math.max(0, milliseconds)}ms`;
	return `${(milliseconds / 1_000).toFixed(1)}s`;
}

function formatBytes(bytes: number): string {
	return bytes < 0 ? "—" : formatSize(bytes);
}
